import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clapperboard, Plus, Trash2, Film } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AccountMenu } from "@/components/AccountMenu";
import { ImportPackage } from "@/components/studio/MoveToLive";

const NONE = "none";

const CreateDialog = ({ open, onOpenChange, onCreated, episodes, initialPrev }) => {
  const [f, setF] = useState({ title: "", setting: "", tone: "", aspect_ratio: "16:9", previous_episode_id: NONE, start_from_previous: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF((x) => ({ ...x, previous_episode_id: initialPrev || NONE })); }, [open, initialPrev]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const prev = episodes.find((e) => e.id === f.previous_episode_id);
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Give the episode a title");
    setBusy(true);
    try {
      const { data } = await http.post("/episodes", { ...f, previous_episode_id: prev ? prev.id : null });
      if (prev) toast.success(`Continuing from "${prev.title}" — locked cast copied over`);
      onCreated(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#11141E] border-[#2A3245]" data-testid="create-episode-dialog">
        <DialogHeader><DialogTitle className="font-display">New episode</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input data-testid="episode-title-input" placeholder="Episode title" value={f.title} onChange={set("title")} />
          <div className="space-y-2">
            <div className="font-mono text-[10px] tracking-widest text-cyan-400">CONTINUES FROM</div>
            <Select value={f.previous_episode_id} onValueChange={(v) => setF({ ...f, previous_episode_id: v })}>
              <SelectTrigger data-testid="episode-previous-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Nothing — a fresh start</SelectItem>
                {episodes.map((e) => <SelectItem key={e.id} value={e.id}>{e.title} · {e.scene_count} scenes · {new Date(e.created_at).toLocaleDateString()}</SelectItem>)}
              </SelectContent>
            </Select>
            {prev && (
              <>
                <p className="text-[11px] text-slate-400" data-testid="previous-episode-note">
                  Copies every locked character (same looks and voices), the setting, tone and format. AI script writing picks up from its ending.
                </p>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <Checkbox data-testid="start-from-previous-checkbox" checked={f.start_from_previous} onCheckedChange={(v) => setF({ ...f, start_from_previous: !!v })} />
                  Start scene 1 from the final frame of "{prev.title}"
                </label>
              </>
            )}
          </div>
          <Textarea data-testid="episode-setting-input" placeholder={prev ? "Setting (leave empty to keep the previous one)" : "Overall setting (e.g. a busy chai café in Mumbai, present day)"} value={f.setting} onChange={set("setting")} />
          <Textarea data-testid="episode-tone-input" placeholder={prev ? "Visual tone (leave empty to keep the previous one)" : "Visual tone (e.g. warm, natural light, handheld, realistic drama)"} value={f.tone} onChange={set("tone")} />
          {!prev && (
            <Select value={f.aspect_ratio} onValueChange={(v) => setF({ ...f, aspect_ratio: v })}>
              <SelectTrigger data-testid="episode-aspect-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="16:9">16:9 landscape (TV / YouTube)</SelectItem>
                <SelectItem value="9:16">9:16 vertical (Reels / Shorts)</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button data-testid="episode-create-submit" disabled={busy} onClick={submit} className="w-full rounded-full">
            {busy ? "Creating…" : prev ? "Create next episode" : "Create episode"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function EpisodesPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [eps, setEps] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => http.get("/episodes").then((r) => setEps(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("continue")) setOpen(true); }, [params]);
  const onOpenChange = (v) => { setOpen(v); if (!v && params.get("continue")) setParams({}); };
  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this episode and all its scenes?")) return;
    await http.delete(`/episodes/${id}`);
    load();
  };

  return (
    <div className="min-h-screen px-6 md:px-14 py-12 max-w-6xl mx-auto" data-testid="episodes-page">
      <div className="flex items-center gap-3 text-amber-400 rise">
        <Clapperboard className="w-5 h-5" /><span className="font-mono text-xs tracking-[0.3em]">EPISODE STUDIO</span>
        <div className="ml-auto"><AccountMenu /></div>
      </div>
      <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6 rise" style={{ animationDelay: "80ms" }}>
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Your episodes</h1>
          <p className="mt-4 text-slate-400 max-w-xl text-sm md:text-base">
            Script in, 20-second live-action scenes out. Same faces, same voices, scene after scene.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <ImportPackage episodes={eps} onDone={load} />
          <Button data-testid="new-episode-btn" onClick={() => setOpen(true)} className="rounded-full h-11 px-6">
            <Plus className="w-4 h-4 mr-2" /> New episode
          </Button>
        </div>
      </div>

      <div className="mt-14 space-y-3">
        {eps.length === 0 && (
          <div className="panel p-10 text-slate-400 text-sm" data-testid="episodes-empty">
            No episodes yet. Create one to start writing or pasting your script.
          </div>
        )}
        {eps.map((e, i) => (
          <div key={e.id} data-testid={`episode-row-${e.id}`} onClick={() => nav(`/episode/${e.id}`)}
            className="panel p-5 flex items-center gap-5 cursor-pointer hover:border-amber-500/50 transition-colors rise"
            style={{ animationDelay: `${120 + i * 50}ms` }}>
            <div className="w-12 h-12 rounded-md bg-[#181D2B] grid place-items-center text-amber-400"><Film className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg truncate">{e.title}</div>
              <div className="text-xs text-slate-500 truncate">
                {e.imported_at && <span className="text-violet-300 mr-2" data-testid={`imported-badge-${e.id}`}>⇪ imported {new Date(e.imported_at).toLocaleString()}</span>}
                {e.previous_episode_id && <span className="text-cyan-400 mr-2">↳ follows {eps.find((x) => x.id === e.previous_episode_id)?.title || "a deleted episode"}</span>}
                {e.setting || "No setting yet"}
              </div>
            </div>
            <div className="font-mono text-xs text-slate-400 hidden sm:block">
              {e.approved_count}/{e.scene_count} scenes approved
            </div>
            <button data-testid={`delete-episode-${e.id}`} onClick={(ev) => remove(ev, e.id)} className="text-slate-500 hover:text-red-400 transition-colors p-2">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <CreateDialog open={open} onOpenChange={onOpenChange} onCreated={(d) => nav(`/episode/${d.id}`)} episodes={eps} initialPrev={params.get("continue")} />
    </div>
  );
}
